// SIMS-8368 (FR-12) — the shared, validated certificate configuration contract.
//
// This module is the SINGLE source of truth for the certificate configuration, consumed by
// BOTH the settings editor (CertificateConfigPicker) and the renderer (LaunchCertificateModal).
// Before this contract, the shape was defined twice with divergent defaults and read through an
// `any` cast, so a field could be written on one side and silently dropped on the other. The
// typed shape + one shared default set + one parser close that gap (the anti-drift guarantee).

/** A hosted logo with its configured display dimensions. */
export interface CertificateLogo {
	url: string;
	width: number;
	height: number;
}

/** The footer labels shown beneath the coordinator name and completion date. */
export interface CertificateFooterLabels {
	coordinator: string;
	date: string;
}

/**
 * The full certificate configuration. Every field the settings editor writes and the renderer
 * reads lives here — nothing implicit.
 *
 * Rendering for border / background (SIMS-8349) and trophy / badge (SIMS-8350) is now wired, so
 * every field below is both written by the editor AND drawn by the renderer.
 */
export interface CertificateConfig {
	accountLogo: CertificateLogo;
	secondaryLogo: CertificateLogo;
	titleText: string;
	titleColor: string;
	/**
	 * SIMS-8416 (FR-8): the certificate TITLE's font. One of the hand-off's curated options, or the
	 * sentinel `match-body` meaning "use the certificate's own body font". Title only — body-text font
	 * configurability is out of scope (OQ-6).
	 */
	titleFont: string;
	titleFontSize: number;
	primaryTextColor: string;
	secondaryTextColor: string;
	congratulationsText: string;
	completionMessageTemplate: string;
	/** SIMS-8424 (FR-15): replaces the enrollment's program name on the certificate label when set. */
	programLabelOverride: string;
	footerLabels: CertificateFooterLabels;
	showCoordinatorName: boolean;
	coordinatorTitleOverride: string;
	showTrophyIcon: boolean;
	trophyColor: string;
	showBadgesIcon: boolean;
	badgeColor: string;
	borderStyle: string;
	borderColor: string;
	backgroundColor: string;
	backgroundImage: string;
}

/**
 * The ONE shared default set. Both the editor and the renderer derive their defaults from here,
 * so an un-customized account renders identically no matter which side produced it.
 *
 * NOTE (open question Q1, SIMS-8348): the color defaults are the current RENDERER values (what
 * un-customized accounts render today — FR-13 no-regression). If the PO/designer confirm the SIMS
 * design-variant tokens instead, change them HERE only — every consumer follows automatically.
 */
export const CERTIFICATE_CONFIG_DEFAULTS: Readonly<CertificateConfig> =
	Object.freeze({
		accountLogo: { url: "", width: 200, height: 50 },
		secondaryLogo: { url: "", width: 200, height: 90 },
		titleText: "Certificate of Completion",
		titleColor: "#C67801",
		// The pre-8416 renderer hardcoded Georgia for the title, so this default keeps an
		// un-customized certificate byte-identical (FR-13 no-regression).
		titleFont: "Georgia",
		titleFontSize: 48,
		primaryTextColor: "#121212",
		secondaryTextColor: "#016375",
		congratulationsText: "Congratulations to:",
		completionMessageTemplate: "For Completing the {orgName} {programName}!",
		// SIMS-8424 (FR-15): blank means "show the enrollment's program name" — the label is never
		// empty, so clearing the override restores the dynamic name rather than hiding it (C16015).
		programLabelOverride: "",
		footerLabels: {
			coordinator: "CM Coordinator/Supervisor",
			date: "Date of Completion",
		},
		showCoordinatorName: true,
		coordinatorTitleOverride: "",
		// SIMS-8400 / SIMS-8408 (FR-5 / FR-6): default OFF. These were `true` under SIMS-8348 only
		// because NOTHING rendered them — the visible default was "no icons". Now that SIMS-8350 wires
		// the rendering, `true` would put a trophy and a badge on every un-customized certificate,
		// breaking FR-13 no-regression. TestRail C15992 / C15984 state it outright: "the default must
		// be off".
		showTrophyIcon: false,
		// SIMS-8400 (FR-7): gold. A literal, not a design-system token — the certificate artwork is
		// account-branded, not product-branded, so the Hi-Fi deliberately keeps these outside the
		// product themes.
		trophyColor: "#C69A2E",
		showBadgesIcon: false,
		// SIMS-8408 (FR-7): teal. Same account-branded rationale as trophyColor.
		badgeColor: "#1C8A82",
		borderStyle: "gold-ornamental",
		borderColor: "#DAA520",
		backgroundColor: "#FFFFFF",
		backgroundImage: "",
	});

/**
 * SIMS-8416 (FR-8) — the curated title-font list from the Hi-Fi hand-off, each mapped to a full CSS
 * stack ending in a generic family.
 *
 * "PDF-safe" in the hand-off means "already on everybody's computer", NOT "PDF-embeddable": the PDF is
 * a raster capture of the DOM (html2canvas), so a font missing on the capturing machine substitutes
 * SILENTLY — no error, just the wrong typeface on the certificate. Hence the full stacks: Garamond and
 * Palatino in particular vary by platform (Palatino is `Palatino Linotype` on Windows, Garamond ships
 * with Office rather than the OS), so each needs real alternates plus a generic terminator.
 *
 * Roboto is the one non-system entry; it is self-hosted via @fontsource (see index.tsx), so it cannot
 * substitute. That is why the PO's answer was "keep including Roboto" rather than dropping it — the
 * MATCH_BODY option below resolves to it too.
 */
export const CERTIFICATE_TITLE_FONTS: Readonly<Record<string, string>> =
	Object.freeze({
		Georgia: "'Georgia', 'Times New Roman', serif",
		"Times New Roman": "'Times New Roman', Times, serif",
		Garamond: "'Garamond', 'EB Garamond', 'Times New Roman', serif",
		Palatino:
			"'Palatino Linotype', 'Palatino', 'Book Antiqua', 'Georgia', serif",
		Roboto: "'Roboto', 'Helvetica', 'Arial', sans-serif",
		Arial: "'Arial', 'Helvetica', sans-serif",
		Helvetica: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
		Verdana: "'Verdana', 'Geneva', sans-serif",
		Trebuchet: "'Trebuchet MS', 'Lucida Grande', 'Tahoma', sans-serif",
	});

/**
 * The sentinel for the hand-off's "Match body text" option. Stored rather than a resolved family, so
 * the title TRACKS the certificate's body font instead of freezing today's value — TestRail C16022
 * requires it to follow the effective body font "in both directions".
 */
export const CERTIFICATE_TITLE_FONT_MATCH_BODY = "match-body";

/**
 * The certificate's own body font — what "Match body text" resolves to. The hand-off's body copy (the
 * program label, congratulations line and completion message) carries no font-family and inherits the
 * page chrome's Roboto; the PO confirmed the reading: "it means the certificate's own body text, so
 * the title and the wording beneath it look like a set".
 */
export const CERTIFICATE_BODY_FONT =
	"'Roboto', 'Helvetica', 'Arial', sans-serif";

/** Resolve a configured `titleFont` to a CSS font stack, falling back to the default when unknown. */
export function resolveTitleFont(titleFont: string): string {
	if (titleFont === CERTIFICATE_TITLE_FONT_MATCH_BODY) {
		return CERTIFICATE_BODY_FONT;
	}
	return (
		CERTIFICATE_TITLE_FONTS[titleFont] ??
		CERTIFICATE_TITLE_FONTS[CERTIFICATE_CONFIG_DEFAULTS.titleFont]
	);
}

/** True when `color` is a valid 3- or 6-digit hex string (e.g. `#abc` / `#A1B2C3`). */
export function isValidHex(color: unknown): boolean {
	return (
		typeof color === "string" &&
		/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)
	);
}

/** Coerce an unknown to a non-empty string, else the default. */
function str(value: unknown, fallback: string): string {
	return typeof value === "string" && value.length > 0 ? value : fallback;
}

/** Coerce an unknown to a string that may be empty (URLs, overrides), else the default. */
function strAllowEmpty(value: unknown, fallback: string): string {
	return typeof value === "string" ? value : fallback;
}

/** Coerce an unknown to a finite number, else the default. */
function num(value: unknown, fallback: number): number {
	const n = Number(value);
	return Number.isFinite(n) && value !== "" && value !== null ? n : fallback;
}

/** Coerce an unknown to a boolean, else the default (explicit-only, so `false` is honored). */
function bool(value: unknown, fallback: boolean): boolean {
	return typeof value === "boolean" ? value : fallback;
}

/** A validated hex color, or the default when absent / non-string / malformed. */
function color(value: unknown, fallback: string): string {
	return isValidHex(value) ? (value as string) : fallback;
}

function logo(value: unknown, fallback: CertificateLogo): CertificateLogo {
	const v = (value ?? {}) as Record<string, unknown>;
	return {
		url: strAllowEmpty(v.url, fallback.url),
		width: num(v.width, fallback.width),
		height: num(v.height, fallback.height),
	};
}

/**
 * Parse ANY raw setting value into a complete, valid `CertificateConfig`.
 *
 * Handles the three real-world states with one code path (the anti-drift contract):
 *  - **absent**  — `undefined` / `null` / `""` / malformed JSON → the full default set,
 *  - **partial** — a field missing → that field's default,
 *  - **invalid** — a field present but wrong type / bad color → that field's safe default.
 *
 * The result ALWAYS carries every contract key, so no consumer can read `undefined`.
 */
export function parseCertificateConfig(raw: unknown): CertificateConfig {
	const d = CERTIFICATE_CONFIG_DEFAULTS;

	let obj: Record<string, unknown>;
	if (typeof raw === "string") {
		try {
			obj = JSON.parse(raw) as Record<string, unknown>;
		} catch {
			obj = {};
		}
	} else if (typeof raw === "object" && raw !== null) {
		obj = raw as Record<string, unknown>;
	} else {
		obj = {};
	}

	const footer = (obj.footerLabels ?? {}) as Record<string, unknown>;

	return {
		accountLogo: logo(obj.accountLogo, d.accountLogo),
		secondaryLogo: logo(obj.secondaryLogo, d.secondaryLogo),
		titleText: str(obj.titleText, d.titleText),
		titleColor: color(obj.titleColor, d.titleColor),
		titleFont: str(obj.titleFont, d.titleFont),
		titleFontSize: num(obj.titleFontSize, d.titleFontSize),
		primaryTextColor: color(obj.primaryTextColor, d.primaryTextColor),
		secondaryTextColor: color(obj.secondaryTextColor, d.secondaryTextColor),
		congratulationsText: str(obj.congratulationsText, d.congratulationsText),
		completionMessageTemplate: str(
			obj.completionMessageTemplate,
			d.completionMessageTemplate,
		),
		// strAllowEmpty, not str: an empty override is MEANINGFUL (it means "use the program name"),
		// so it must round-trip as "" rather than being coerced to a default.
		programLabelOverride: strAllowEmpty(
			obj.programLabelOverride,
			d.programLabelOverride,
		),
		footerLabels: {
			coordinator: str(footer.coordinator, d.footerLabels.coordinator),
			date: str(footer.date, d.footerLabels.date),
		},
		showCoordinatorName: bool(obj.showCoordinatorName, d.showCoordinatorName),
		coordinatorTitleOverride: strAllowEmpty(
			obj.coordinatorTitleOverride,
			d.coordinatorTitleOverride,
		),
		showTrophyIcon: bool(obj.showTrophyIcon, d.showTrophyIcon),
		// An invalid colour falls back to the default rather than hiding the icon (C15996 / C15987).
		trophyColor: color(obj.trophyColor, d.trophyColor),
		showBadgesIcon: bool(obj.showBadgesIcon, d.showBadgesIcon),
		badgeColor: color(obj.badgeColor, d.badgeColor),
		borderStyle: str(obj.borderStyle, d.borderStyle),
		borderColor: color(obj.borderColor, d.borderColor),
		backgroundColor: color(obj.backgroundColor, d.backgroundColor),
		backgroundImage: strAllowEmpty(obj.backgroundImage, d.backgroundImage),
	};
}
